import { z } from "zod"

export const PAYWALL_STRIPE_AMOUNTS_FLAG = "paywall-stripe-live-prices"

export const PAYWALL_TIERS = ["starter", "premium", "ultimate"] as const
export type PaywallTier = (typeof PAYWALL_TIERS)[number]

const PRICE_ENV = {
  starter: {
    monthly: "STRIPE_LIVE_PRICE_STARTER_MONTHLY",
    annual: "STRIPE_LIVE_PRICE_STARTER_YEARLY",
  },
  premium: {
    monthly: "STRIPE_LIVE_PRICE_PREMIUM_MONTHLY",
    annual: "STRIPE_LIVE_PRICE_PREMIUM_YEARLY",
  },
  ultimate: {
    monthly: "STRIPE_LIVE_PRICE_ULTIMATE_MONTHLY",
    annual: "STRIPE_LIVE_PRICE_ULTIMATE_YEARLY",
  },
} as const

export interface StripePriceRecord {
  id: string
  unitAmount: number
  interval: "month" | "year"
  active: boolean
}

export interface PaywallSlotAmount {
  monthly: number
  annual: number
  annualMonthlyEquivalent: number
  monthlyPriceId: string
  annualPriceId: string
}

export type PaywallAmounts = Record<PaywallTier, PaywallSlotAmount>

export function stripePaywallAmountsEnabled(flag: unknown): boolean {
  return flag === true || flag === "true"
}

export function dollarsFromUnitAmount(unitAmount: number): number {
  if (!Number.isInteger(unitAmount) || unitAmount < 0) {
    throw new Error("stripe unit amount is not whole cents")
  }
  return unitAmount / 100
}

export function paywallAmountsFromStripe(
  prices: ReadonlyMap<string, StripePriceRecord>,
  env: Record<string, string | undefined>,
): PaywallAmounts {
  const amounts = {} as PaywallAmounts
  for (const tier of PAYWALL_TIERS) {
    const monthlyId = env[PRICE_ENV[tier].monthly]
    const annualId = env[PRICE_ENV[tier].annual]
    if (!monthlyId || !annualId) throw new Error("missing stripe price id")
    const monthly = requirePrice(prices, monthlyId, "month")
    const annual = requirePrice(prices, annualId, "year")
    amounts[tier] = {
      monthly: dollarsFromUnitAmount(monthly.unitAmount),
      annual: dollarsFromUnitAmount(annual.unitAmount),
      annualMonthlyEquivalent: Math.round(annual.unitAmount / 12) / 100,
      monthlyPriceId: monthly.id,
      annualPriceId: annual.id,
    }
  }
  return amounts
}

function requirePrice(
  prices: ReadonlyMap<string, StripePriceRecord>,
  id: string,
  interval: "month" | "year",
): StripePriceRecord {
  const price = prices.get(id)
  if (!price || !price.active || price.interval !== interval || price.id !== id) {
    throw new Error("inactive or mismatched stripe price")
  }
  return price
}

export function assertShownAmountsMatchStripe(
  shown: PaywallAmounts,
  stripe: PaywallAmounts,
): void {
  for (const tier of PAYWALL_TIERS) {
    if (shown[tier].monthly !== stripe[tier].monthly) {
      throw new Error(`${tier} monthly amount differs from stripe`)
    }
    if (shown[tier].annual !== stripe[tier].annual) {
      throw new Error(`${tier} annual amount differs from stripe`)
    }
    if (shown[tier].monthlyPriceId !== stripe[tier].monthlyPriceId) {
      throw new Error(`${tier} monthly price id differs`)
    }
    if (shown[tier].annualMonthlyEquivalent !== stripe[tier].annualMonthlyEquivalent) {
      throw new Error(`${tier} annual monthly equivalent differs from stripe`)
    }
    if (shown[tier].annualPriceId !== stripe[tier].annualPriceId) {
      throw new Error(`${tier} annual price id differs`)
    }
  }
}

export async function loadPaywallAmounts(
  env: Record<string, string | undefined>,
  retrieve: (id: string) => Promise<StripePriceRecord>,
): Promise<PaywallAmounts> {
  const ids = PAYWALL_TIERS.flatMap(tier => [
    env[PRICE_ENV[tier].monthly],
    env[PRICE_ENV[tier].annual],
  ])
  const prices = new Map<string, StripePriceRecord>()
  for (const id of ids) {
    if (!id) throw new Error("missing stripe price id")
    prices.set(id, await retrieve(id))
  }
  return paywallAmountsFromStripe(prices, env)
}

export function yearlySavingsFromStripe(slot: PaywallSlotAmount): number {
  return Math.round((slot.monthly * 12 - slot.annual) * 100) / 100
}

const paywallSlotSchema = z.object({
  monthly: z.number().nonnegative(),
  annual: z.number().nonnegative(),
  annualMonthlyEquivalent: z.number().nonnegative(),
  monthlyPriceId: z.string().min(1),
  annualPriceId: z.string().min(1),
})

const paywallAmountsResponseSchema = z.object({
  amounts: z
    .object({
      starter: paywallSlotSchema,
      premium: paywallSlotSchema,
      ultimate: paywallSlotSchema,
    })
    .nullable(),
})

/** Checks the /api/paywall-amounts body at runtime; anything malformed falls back to static amounts (null). */
export function parsePaywallAmountsResponse(body: unknown): PaywallAmounts | null {
  const parsed = paywallAmountsResponseSchema.safeParse(body)
  return parsed.success ? parsed.data.amounts : null
}

/** The one yearly-savings amount every surface shows: Stripe when loaded, else the static display math. */
export function yearlySavingsShown(
  monthlyPrice: number,
  staticYearlyMonthly: number,
  slot: PaywallSlotAmount | null | undefined,
): number {
  if (slot) return yearlySavingsFromStripe(slot)
  return Math.round((monthlyPrice - staticYearlyMonthly) * 12 * 100) / 100
}

export function monthlyShownAmount(
  tier: PaywallTier,
  fallback: number,
  amounts: PaywallAmounts | null,
): number {
  return amounts?.[tier].monthly ?? fallback
}
